import type { ColorProps } from '@chakra-ui/react'
import { faCheck, faTimes, type IconDefinition } from '@fortawesome/pro-regular-svg-icons'
import { isEqual } from 'lodash-es'
import { InlineMath } from 'react-katex'

import { pluralize } from '@/utils/helpers'
import logger from '@/utils/logger'

import countCharacters from '@/client/services/richText/countCharacters'
import isEmpty from '@/client/services/richText/isEmpty'
import isSerializedRichTextProbably from '@/client/services/richText/isSerializedRichTextProbably'

import FormContentBlock from './FormContentBlock'
import type {
  FormBlock,
  FormErrors,
  FormInputType,
  FormResult,
  FormRow,
  FormValue,
  FormValues,
} from './types'

export const ROW_SIZE = 6

export const isInput = (block: FormBlock): block is FormInputType =>
  block.display === undefined || block.display === 'input'

export function blocksToRows(blocks: FormBlock[]): FormBlock[][] {
  const rows: FormBlock[][] = []
  let currentRow: FormBlock[] = []
  let currentSize = 0

  blocks.forEach(block => {
    currentRow.push(block)
    currentSize += block.sizeSpan ?? block.size

    if (currentSize === ROW_SIZE) {
      rows.push(currentRow)
      currentRow = []
      currentSize = 0
    } else if (currentSize > ROW_SIZE) {
      logger.error(`Invalid block size; they don't make up rows of ${ROW_SIZE} correctly`, {
        blocks,
      })
      rows.push(currentRow)
      currentRow = []
      currentSize = 0
    }
  })

  if (currentRow.length > 0) {
    rows.push(currentRow)
  }

  return rows
}

export function basicValidate(
  values: FormValues,
  blocks: FormBlock[]
): Record<string, string> | null {
  let hasError = false
  const errors: FormErrors = {}

  blocks.forEach(block => {
    // Do not validate hidden inputs
    if (block.display === 'display') {
      return
    }
    // Do not validate hidden inputs
    if (
      block.required !== true ||
      (block.dependencies != null && !block.dependencies.callback(values))
    ) {
      return
    }
    const value = values[block.name]
    let missing = false
    let otherErrorMessage: string | null = null
    if (
      block.type === 'multi-checkbox' ||
      block.type === 'multi-select' ||
      block.type === 'multi-select-tab'
    ) {
      missing = value == null || !Array.isArray(value) || value.length === 0
    } else if (block.type === 'date') {
      missing = value == null
    } else if (block.type === 'text' || block.type === 'textarea' || block.type === 'rich-text') {
      let length: number
      if (block.type === 'rich-text') {
        const isRichText = isSerializedRichTextProbably(value)
        missing = value == null || !isRichText || isEmpty(value)
        length = isRichText ? countCharacters(value) : 0
      } else {
        missing = value == null || typeof value !== 'string' || value.length === 0
        length = !missing && typeof value === 'string' ? value.length : 0
      }
      // If the min length constraint is set, and there's no text to speak of or the text is less than the min
      if (block.minLength != null && (missing || length < block.minLength)) {
        otherErrorMessage = `Please write at least ${pluralize(block.minLength, 'character')}`
      }
      // If there's text to speak of, the max length constraint is set, and the text is longer than the max
      if (!missing && block.maxLength != null && length > block.maxLength) {
        otherErrorMessage = `Please write at most ${pluralize(block.maxLength, 'character')}`
      }
    } else if (block.type === 'number') {
      missing = value == null || typeof value !== 'number' || Number.isNaN(value)
    } else if (block.type === 'select-tab') {
      missing = value == null
    } else if (block.type === 'phone') {
      missing = value == null || typeof value !== 'string' || value.length < 10 || value.length > 15
    } else if (block.type === 'time-range') {
      missing = value == null || !Array.isArray(value) || value.length !== 2
    } else {
      missing = value == null || typeof value !== 'string' || value.length === 0
    }

    if (otherErrorMessage != null) {
      hasError = true
      errors[block.name] = otherErrorMessage
    } else if (missing) {
      hasError = true
      errors[block.name] =
        block.type === 'video'
          ? 'Please record and upload this required video'
          : 'This question is required'
    }
  })

  return hasError ? errors : null
}

export function resolveFormDependencies(blockRows: FormBlock[][], values: FormValues): FormRow[] {
  const resolvedRows: FormRow[] = []

  blockRows.forEach(blockRow => {
    // Remove inputs that are hidden by dependencies
    const filteredBlocks = blockRow.filter(
      block => block.dependencies == null || block.dependencies.callback(values)
    )

    if (filteredBlocks.length === 0) {
      // Remove rows with no visible inputs
      return
    }

    const key = blockRow.map(block => block.name).join('$')
    resolvedRows.push({ blocks: filteredBlocks, key })
  })

  return resolvedRows
}

export function filterDependentFormInputValues({
  blocks,
  values,
}: {
  blocks: FormBlock[]
  values: FormValues
}): FormValues {
  const filteredValues: FormValues = {}
  blocks.forEach(block => {
    if (block.display === 'display') {
      return
    }
    if (block.dependencies == null || block.dependencies.callback(values)) {
      filteredValues[block.name] = values[block.name]
    }
  })
  return filteredValues
}

export type QuestionResultStatus = 'not-graded' | 'correct' | 'incorrect' | 'incorrect-not-chosen'

export const QUESTION_RESULT_STATUS_TO_UI_DETAILS: Record<
  QuestionResultStatus,
  {
    textColor: ColorProps['color']
    darkTextColor: ColorProps['color']
    bgColor: ColorProps['color']
    hoverBgColor: ColorProps['color']
    borderColor?: ColorProps['color']
    icon: IconDefinition | null
  }
> = {
  'not-graded': {
    bgColor: 'bg.subtle',
    hoverBgColor: 'bg.muted',
    textColor: 'fg.muted',
    darkTextColor: 'fg',
    icon: null,
  },
  correct: {
    bgColor: 'bg.success',
    hoverBgColor: 'success.subtle',
    textColor: 'success.fg',
    darkTextColor: 'success.fg',
    borderColor: 'green.solid',
    icon: faCheck,
  },
  incorrect: {
    bgColor: 'bg.error',
    hoverBgColor: 'error.subtle',
    textColor: 'error.fg',
    darkTextColor: 'error.fg',
    borderColor: 'red.solid',
    icon: faTimes,
  },
  'incorrect-not-chosen': {
    bgColor: 'bg',
    hoverBgColor: 'bg.muted',
    textColor: 'success.fg',
    darkTextColor: 'fg',
    borderColor: 'green.solid',
    icon: faCheck,
  },
}

function normalizeEquation(equation: string): string {
  return equation.replace(/\\|\s+/g, '')
}

export function gradeQuestion(
  input: FormInputType | FormResult,
  response: FormValue
): QuestionResultStatus {
  const answers = input.metadata?.answers ?? []
  if (answers.length === 0) {
    return 'not-graded'
  }
  if (Array.isArray(response)) {
    const correct = isEqual(answers.sort(), response.sort())
    return correct ? 'correct' : 'incorrect'
  }
  if (input.type === 'equation') {
    // TODO: Use a LaTeX parsing library to compare equations for real?
    const correct = answers
      .map(answer => normalizeEquation(`${answer}`))
      .includes(normalizeEquation(`${response}`))
    return correct ? 'correct' : 'incorrect'
  }
  const correct = answers.includes(`${response}`)
  return correct ? 'correct' : 'incorrect'
}

export function getFormValueToDisplay(
  input: FormInputType | FormResult,
  value: FormValue
): React.ReactNode {
  if (value == null) {
    return '-'
  }

  if ('options' in input) {
    const options =
      input.type === 'select-group' ? input.options.flatMap(o => o.options) : input.options

    return <FormContentBlock content={options.find(o => o.value === value)?.label} />
  }

  if (input.type === 'equation') {
    return <InlineMath math={value} />
  }

  return <FormContentBlock content={value} />
}
